import { sdkEvents } from '../core/events.js';
import { _finishHttpPerf, _startHttpPerf } from '../devtool/perf-counters.js';
import { newCorrelationId } from '../errors/correlation.js';

export type HttpRequestOutcome = 'success' | 'http_error' | 'network_error' | 'cancelled';

export interface HttpRequestTelemetry {
  readonly operationName: string;
  readonly routeTemplate: string;
  readonly requestKind: 'query' | 'mutation';
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly url: string;
  readonly init: RequestInit;
  readonly signal?: AbortSignal;
  readonly retryCount?: number;
}

const attemptsBySignal = new WeakMap<AbortSignal, number>();
const responseStatusBySignal = new WeakMap<AbortSignal, number>();

/** Preserve soft 401 responses for scheduling without changing anonymous hook data. */
export function getHttpResponseStatus(signal: AbortSignal | undefined): number | undefined {
  return signal === undefined ? undefined : responseStatusBySignal.get(signal);
}

/**
 * Fetch a Morfi API route and emit exactly one privacy-safe HTTP telemetry
 * event for the attempt. Concrete URLs and query values stay local; only the
 * manifest route template reaches the telemetry bus.
 */
export async function fetchWithHttpTelemetry(input: HttpRequestTelemetry): Promise<Response> {
  const retryCount = input.retryCount ?? nextRetryCount(input.signal);
  const headers = new Headers(input.init.headers);
  const correlationId = headers.get('x-correlation-id') ?? newCorrelationId();
  headers.set('x-correlation-id', correlationId);
  const startedAt = Date.now();
  let outcome: HttpRequestOutcome | undefined;
  if (process.env.NODE_ENV !== 'production') {
    _startHttpPerf();
  }

  try {
    const response = await fetch(input.url, { ...input.init, headers });
    if (input.signal !== undefined) responseStatusBySignal.set(input.signal, response.status);
    outcome = response.ok ? 'success' : 'http_error';
    emitHttpRequest(input, {
      correlationId,
      durationMs: Date.now() - startedAt,
      outcome,
      retryCount,
      statusCode: response.status,
    });
    if (response.ok) clearAttempts(input.signal);
    return response;
  } catch (error: unknown) {
    outcome = isCancellation(error, input.signal) ? 'cancelled' : 'network_error';
    emitHttpRequest(input, {
      correlationId,
      durationMs: Date.now() - startedAt,
      outcome,
      retryCount,
    });
    if (outcome === 'cancelled') clearAttempts(input.signal);
    throw error;
  } finally {
    if (process.env.NODE_ENV !== 'production') {
      _finishHttpPerf(outcome === 'cancelled');
    }
  }
}

interface HttpRequestResult {
  readonly correlationId: string;
  readonly durationMs: number;
  readonly outcome: HttpRequestOutcome;
  readonly retryCount: number;
  readonly statusCode?: number;
}

function emitHttpRequest(input: HttpRequestTelemetry, result: HttpRequestResult): void {
  sdkEvents.emit('http:request', {
    method: input.method,
    operationName: input.operationName,
    routeTemplate: input.routeTemplate,
    requestKind: input.requestKind,
    outcome: result.outcome,
    retryCount: result.retryCount,
    correlationId: result.correlationId,
    durationMs: result.durationMs,
    ...(result.statusCode === undefined ? {} : { statusCode: result.statusCode }),
  });
}

function nextRetryCount(signal: AbortSignal | undefined): number {
  if (signal === undefined) return 0;
  const retryCount = attemptsBySignal.get(signal) ?? 0;
  attemptsBySignal.set(signal, retryCount + 1);
  return retryCount;
}

function clearAttempts(signal: AbortSignal | undefined): void {
  if (signal !== undefined) attemptsBySignal.delete(signal);
}

function isCancellation(error: unknown, signal: AbortSignal | undefined): boolean {
  if (signal?.aborted === true) return true;
  return error instanceof Error && error.name === 'AbortError';
}
